// slices/chat/sendChatTurn.ts
import type { Dispatch } from 'redux';
import { v4 as uuidv4 } from 'uuid';
import { Capacitor } from '@capacitor/core';
import {
  runChatTurnStream,
  type ChatStreamDeps,
  type ChatStreamOutcome,
  type ChatStreamRun,
} from '../../services/chatStream/chatStreamClient';
import {
  clearChatSelectionAndPrefill,
  getChatAppContextInputs,
} from '../../chatContext/chatAppContextStore';
import {
  getCurrentAppContextEnvelope,
  withScreenSnapshot,
} from '../../chatContext/currentAppContextEnvelope';
import type { ChatTurnRequest } from '../../types/chatStream';
import {
  selectEnvelopeForRegenerate,
  type ChatRootState,
} from './chatSelectors';
import type { ChatMessageAttachment } from './chatSlice';
import {
  conversationBlockThreadSet,
  isDraftConversationId,
  streamEventReceived,
  streamEventsReceived,
  streamEventSkipped,
  streamFailed,
  turnAborted,
  turnDiscarded,
  turnRequested,
} from './chatSlice';
import { createFrameScheduler } from '../../services/chatStream/frameScheduler';
import type { ChatStreamEvent } from '../../types/chatStream';
import { registerChatSessionClearer } from './chatSessionLifecycle';
import { blockThreadOfTurn, withBlockThreadScope } from './blockThreadScope';
import type { ChatPlatform } from '../../types/chatEnvelope';

export interface SendChatTurnArgs {
  request: ChatTurnRequest;
  /** Client turn id. Generated when omitted; the key for the turn in the slice. */
  turnId?: string;
  /**
   * Existing user message id. Retry and regenerate pass the id of the message
   * already in the thread so it is not repeated.
   */
  userMessageId?: string;
  /** Local document display rows, kept out of the turn wire body. */
  userMessageAttachments?: ChatMessageAttachment[];
  assistantMessageId?: string;
  /**
   * Assistant messages this turn replaces in the thread. A regenerate hides
   * the answer it re-runs, the way the conversation read does (ORA-459).
   */
  supersedes?: string[];
  /** Injected in tests; production uses `createChatStreamDeps()`. */
  deps?: ChatStreamDeps;
}

/**
 * One entry per in-flight turn, registered before anything is awaited. The run
 * itself only exists a microtask later (production deps are imported lazily),
 * so an abort dispatched in the same tick as the send would otherwise find an
 * empty map, leave the request running, and mark the turn aborted anyway. The
 * flag closes that window: whoever arrives second does the aborting.
 */
interface ActiveTurn {
  run: ChatStreamRun | null;
  aborted: boolean;
  /** True when account teardown must not dispatch a late terminal event. */
  discarded: boolean;
  /**
   * Apply any deltas still buffered for the frame. Stopping a turn must not
   * throw away text that has already arrived, and `turnAborted` closes the turn
   * to further events — so the stopper flushes before it ends the turn.
   */
  flush: () => void;
  /** Drop scheduled display work when the whole account session ends. */
  discard: () => void;
}

const activeTurns = new Map<string, ActiveTurn>();

/** Keep the wire value inside the backend envelope's closed platform enum. */
const getChatPlatform = (): ChatPlatform => {
  const platform = Capacitor.getPlatform();
  return platform === 'ios' || platform === 'android' ? platform : 'web';
};

/**
 * Decide what app context this turn carries, and consume whatever it spends.
 *
 * Order:
 * 1. A retry or regenerate replays the envelope its original turn was sent
 *    with. The student is redoing that question from where they asked it, not
 *    from wherever they have wandered since — and it must not consume a
 *    selection they have made in the meantime. When the original turn is not
 *    in the slice (a conversation reopened from history has messages but no
 *    turns) the re-ask carries *no* envelope at all rather than borrowing the
 *    current surface's: a "go deeper" on last week's answer is not being asked
 *    from wherever the student is standing today, and borrowing would also
 *    silently spend a selection they made for some other question.
 * 2. An explicit envelope (ORA-447's composer builds one from the same hook).
 * 3. The envelope published by the visible surface.
 *
 * Called synchronously with the send, before anything is awaited: the deps are
 * imported lazily, and a navigation inside that await would otherwise swap the
 * context out from under the turn.
 *
 * Selection and prefill are one-shot. They are cleared when — and only when —
 * the envelope actually going out carries them, so a send from a surface that
 * never saw them cannot silently discard a pending highlight.
 */
const withAppContextEnvelope = (
  request: ChatTurnRequest,
  getState?: () => ChatRootState
): ChatTurnRequest => {
  if (request.regenerateOf) {
    const replayed = getState
      ? selectEnvelopeForRegenerate(getState(), request.regenerateOf)
      : null;

    // Either way this returns without touching the one-shot fields.
    return replayed
      ? { ...request, envelope: { ...replayed, platform: getChatPlatform() } }
      : request;
  }

  const envelope = request.envelope ?? getCurrentAppContextEnvelope();

  if (envelope) {
    const inputs = getChatAppContextInputs();

    const spentSelection =
      inputs.selection !== null && envelope.selection === inputs.selection;

    const spentPrefill =
      inputs.prefill !== null && envelope.prefill === inputs.prefill;

    if (spentSelection || spentPrefill) clearChatSelectionAndPrefill();
  }

  // The screen outline is captured here and nowhere else: once per send, at the
  // moment the student asks, off the page they are actually looking at.
  return envelope
    ? {
        ...request,
        envelope: {
          ...withScreenSnapshot(envelope),
          platform: getChatPlatform(),
        },
      }
    : request;
};

/**
 * Take a client-side draft id off the wire request.
 *
 * A draft id names no row on the server, so sending one would 404. The slice
 * hands one back whenever a conversation never got a real id — a first turn
 * that has not reached `turn.start` yet, or one the backend threw away after a
 * terminal first-turn error (ORA-459). Both mean the same thing to the
 * backend: create a conversation for this turn. The turn keeps the draft id
 * locally, so the thread on screen does not move.
 *
 * `regenerateOf` goes with it. It names a message inside the conversation
 * being asked for, and there is no such conversation yet — the retry button on
 * a first turn that died is a fresh question, not a re-run of a stored one.
 */
const withoutDraftConversationId = (
  request: ChatTurnRequest
): ChatTurnRequest => {
  if (!isDraftConversationId(request.conversationId ?? null)) return request;

  const wire = { ...request };
  delete wire.conversationId;
  delete wire.regenerateOf;
  return wire;
};

/** Stop a turn that is still streaming. No-op once it has finished. */
export const abortChatTurn = (turnId: string) => (dispatch: Dispatch) => {
  const active = activeTurns.get(turnId);

  if (active) {
    active.aborted = true;
    active.run?.abort();
    active.flush();
  }

  dispatch(turnAborted({ turnId }));
};

/**
 * Stop every transport belonging to the current browser session.
 *
 * Logout resets the Redux tree itself, so this deliberately does not dispatch:
 * a reducer must not be re-entered while it is processing the logout action.
 * Each stream's completion still settles its promise and removes its map entry.
 */
export const abortAllChatTurns = (): void => {
  activeTurns.forEach((active) => {
    active.aborted = true;
    active.discarded = true;
    active.run?.abort();
    active.discard();
  });
};

registerChatSessionClearer(abortAllChatTurns);

/**
 * Resolve the stream deps. Always asynchronous, even when the caller injects
 * them, so tests run against the same ordering production does: in the app the
 * deps arrive a microtask late because `chatStreamDeps` is imported lazily
 * (it pulls in axios and the store), and that microtask is exactly the window
 * an abort can land in.
 */
const resolveChatStreamDeps = async (
  deps?: ChatStreamDeps
): Promise<ChatStreamDeps> =>
  deps ??
  (
    await import('../../services/chatStream/chatStreamDeps')
  ).createChatStreamDeps();

/**
 * Open a turn and pump its stream into the chat slice.
 *
 * The slice entry is created before the request goes out, so a token refresh
 * and request replay inside the client is invisible here: the turn keeps its
 * client id and whatever state it already had.
 */
export const sendChatTurn =
  (args: SendChatTurnArgs) =>
  async (
    dispatch: Dispatch,
    getState?: () => ChatRootState
  ): Promise<ChatStreamOutcome> => {
    const request = withBlockThreadScope(
      withAppContextEnvelope(args.request, getState)
    );

    const turnId = args.turnId ?? uuidv4();
    const userMessageId = args.userMessageId ?? uuidv4();
    const assistantMessageId = args.assistantMessageId ?? uuidv4();

    dispatch(
      turnRequested({
        turnId,
        conversationId: args.request.conversationId ?? null,
        userMessage: {
          id: userMessageId,
          text: args.request.message,
          attachments: args.userMessageAttachments ?? args.request.attachments,
          documentIds: args.request.documentIds,
        },
        assistantMessageId,
        regenerateOf: args.request.regenerateOf ?? null,
        supersedes: args.supersedes,
        depth: args.request.depth ?? null,
        envelope: request.envelope ?? null,
      })
    );

    const blockThread = blockThreadOfTurn(request, turnId);
    if (blockThread) dispatch(conversationBlockThreadSet(blockThread));

    /**
     * Text deltas are buffered and flushed a frame at a time (ORA-459): a fast
     * answer emits a delta every few milliseconds, and dispatching each one
     * costs a store notification, a re-render, and the thread's layout read for
     * text the screen cannot paint until the next frame anyway.
     *
     * Ordering is the whole game here. Anything that is not a delta — a block,
     * `text.done`, `turn.done`, an error, even a skipped event moving the seq
     * cursor — flushes the buffer first and is then dispatched on its own, so
     * the slice sees exactly the wire order it would have seen unbuffered.
     */
    const buffered: ChatStreamEvent[] = [];
    const frames = createFrameScheduler();

    const flushBufferedEvents = () => {
      frames.cancel();
      if (buffered.length === 0) return;

      dispatch(
        streamEventsReceived({
          turnId,
          events: buffered.splice(0, buffered.length),
        })
      );
    };

    const discardBufferedEvents = () => {
      frames.cancel();
      buffered.splice(0, buffered.length);
    };

    const active: ActiveTurn = {
      run: null,
      aborted: false,
      discarded: false,
      flush: flushBufferedEvents,
      discard: discardBufferedEvents,
    };

    activeTurns.set(turnId, active);

    const deps = await resolveChatStreamDeps(args.deps);

    // Logout or Stop can arrive while the lazily imported transport is loading.
    // Do not even construct a request after a session-ending abort: by then a
    // newly signed-in account may have replaced the auth token the deps read.
    if (active.aborted) {
      discardBufferedEvents();
      activeTurns.delete(turnId);
      return {
        status: 'aborted',
        turnId: null,
        lastSeq: null,
        sawSeqGap: false,
      };
    }

    const run = runChatTurnStream(
      withoutDraftConversationId(request),
      {
        onEvent: (event) => {
          if (active.discarded) return;

          if (event.type === 'text.delta') {
            buffered.push(event);
            frames.schedule(flushBufferedEvents);
            return;
          }

          flushBufferedEvents();
          dispatch(streamEventReceived({ turnId, event }));
        },
        onWarning: (warning) => {
          if (active.discarded) return;

          if (warning.type === 'event_skipped') {
            flushBufferedEvents();
            dispatch(streamEventSkipped({ turnId, seq: warning.seq }));
          }
        },
      },
      deps
    );

    active.run = run;
    // The abort may have arrived while the deps were still loading.
    if (active.aborted) run.abort();

    try {
      const outcome = await run.completed;

      // Whatever the stream did, nothing may be left in the buffer once the
      // turn is over — including a run that ended mid-answer.
      flushBufferedEvents();

      if (!active.discarded && outcome.status === 'failed' && outcome.failure) {
        // A message the route refused for its length never reached the model, so
        // it leaves no turn behind: the composer takes the text back and says
        // why, which is the only thing that can fix it (ORA-750).
        dispatch(
          outcome.failure.code === 'message_too_long'
            ? turnDiscarded({ turnId })
            : streamFailed({ turnId, failure: outcome.failure })
        );
      }

      if (!active.discarded && outcome.status === 'aborted') {
        dispatch(turnAborted({ turnId }));
      }

      return outcome;
    } finally {
      frames.cancel();
      activeTurns.delete(turnId);
    }
  };
